import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, dt, day, apiError } from '../utils/format';
import { advanceKind } from '../utils/advances';
import { useDataTable, TableToolbar, TableFilterRow, useRowPick, PickedChip } from '../components/DataTable';

// Org-wide list of both kinds of advance:
//   Lump-sum       — recovered over months, one installment per payslip.
//   Salary advance — cash taken mid-month, deducted in full from that month's pay.
// Add/recover either on the employee's page.
const TYPES = [['all', 'All types'], ['lumpsum', 'Lump-sum'], ['salary', 'Salary advance']];

const Advances = () => {
  const [loans, setLoans] = useState([]);
  const [salaryAdvances, setSalaryAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('active');
  const [type, setType] = useState('all');

  useEffect(() => {
    setLoading(true);
    api.get(`/employees/advances?status=${status}`).then(({ data }) => {
      setLoans(data.loans || []); setSalaryAdvances(data.salaryAdvances || []);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [status]);

  // Both sources normalized onto one row shape, newest first.
  const rows = useMemo(() => {
    const fromLoans = loans.map((l) => ({
      key: `l${l.id}`, type: 'lumpsum', typeLabel: 'Lump-sum', employeeId: l.employeeId, employee: l.employee,
      given: l.createdAt, kind: advanceKind(l.kind), amount: Number(l.amount),
      installment: Number(l.monthlyInstallment), months: l.months,
      recovered: Number(l.recovered), remaining: Number(l.amount) - Number(l.recovered),
      note: l.note, open: l.status === 'active',
      statusLabel: l.status === 'active' ? `recovering · ${l.months} mo` : 'closed',
    }));
    const fromLedger = salaryAdvances.map((s) => {
      const settled = !!s.settledInPayslipId;
      return {
        key: `s${s.id}`, type: 'salary', typeLabel: 'Salary advance', employeeId: s.employeeId, employee: s.employee,
        given: s.date, kind: `Against ${String(s.month || '').slice(0, 7)} pay`, amount: Number(s.amount),
        installment: null, months: null,
        recovered: settled ? Number(s.amount) : 0, remaining: settled ? 0 : Number(s.amount),
        note: s.note, open: !settled,
        statusLabel: settled ? 'deducted' : 'due this payroll',
      };
    });
    return [...fromLoans, ...fromLedger]
      .filter((r) => type === 'all' || r.type === type)
      .sort((a, b) => new Date(b.given) - new Date(a.given));
  }, [loans, salaryAdvances, type]);

  const outstanding = rows.reduce((s, r) => s + (r.open ? r.remaining : 0), 0);

  const dtA = useDataTable(rows, {
    searchPlaceholder: 'Search employee, note, purpose…',
    searchKeys: [(r) => r.employee?.name, 'note', 'kind', 'typeLabel'],
    columns: [
      { key: 'employee', label: 'Employee', value: (r) => r.employee?.name, placeholder: 'Employee' },
      { key: 'typeLabel', label: 'Type', value: (r) => r.typeLabel, type: 'select' },
      { key: 'kind', label: 'For', value: (r) => r.kind, type: 'select' },
      { key: 'statusLabel', label: 'Status', value: (r) => r.statusLabel, type: 'select' },
    ],
  });
  const pick = useRowPick();

  return (
    <div>
      <div className="section-head">
        <span className="muted small">{rows.length} advances · {inr(outstanding)} outstanding</span>
        <div className="flex gap">
          <select value={type} onChange={(e) => setType(e.target.value)} style={{ width: 160 }}>
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
            <option value="active">Active</option><option value="closed">Closed</option><option value="all">All</option>
          </select>
        </div>
      </div>
      <TableToolbar dt={dtA} right={<PickedChip pick={pick} />} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Employee</th><th>Given</th><th>Type</th><th>For</th><th className="right">Amount</th><th className="right">Installment</th><th className="right">Recovered</th><th className="right">Remaining</th><th>Note</th><th>Status</th></tr>
              <TableFilterRow dt={dtA} cells={['employee', null, 'typeLabel', 'kind', null, null, null, null, null, 'statusLabel']} />
            </thead>
            <tbody>
              {dtA.rows.map((r) => (
                <tr key={r.key} {...pick.markProps(r.key)}>
                  <td {...pick.cellProps(r.key)}><Link to={`/employees/${r.employeeId}`} className="link-strong" onClick={(e) => e.stopPropagation()}>{r.employee?.name || `#${r.employeeId}`}</Link></td>
                  <td className="muted small">{r.type === 'salary' ? day(r.given) : dt(r.given)}</td>
                  <td><span className={`badge ${r.type === 'salary' ? 'badge-blue' : 'badge-gray'}`}>{r.typeLabel}</span></td>
                  <td className="muted small">{r.kind}</td>
                  <td className="right">{inr(r.amount)}</td>
                  <td className="right">{r.installment != null ? `${inr(r.installment)}/mo` : '—'}</td>
                  <td className="right">{inr(r.recovered)}</td>
                  <td className="right">{inr(r.remaining)}</td>
                  <td className="muted">{r.note || '—'}</td>
                  <td><span className={`badge ${r.open ? 'badge-gold' : 'badge-green'}`}>{r.statusLabel}</span></td>
                </tr>
              ))}
              {!dtA.rows.length && <tr><td colSpan={10} className="empty">{dtA.isFiltered ? 'No advances match.' : 'No advances.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Advances;
